import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useMe } from '../App.jsx';
import { ErrorBox, JobCard, SearchIcon, Spinner } from '../components.jsx';

const PinIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
    <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const GEO_ERRORS = {
  1: 'Location is switched off for this app. You can allow it in your phone settings, or just use the search box.',
  2: "Couldn't work out where you are. Try the search box instead.",
  3: "Couldn't get your location in time. Try again, or use the search box.",
};

export default function Search() {
  const { user, canCreateJobs, canFindNearby } = useMe();
  const staff = user.role === 'staff';
  const showStage = user.role !== 'installer'; // warehouse searches every stage, so they need to see which one
  const [q, setQ] = useState('');
  const [jobs, setJobs] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [near, setNear] = useState(null); // { groups, warming } | { loading } | { error }

  useEffect(() => {
    api.get('/api/jobs/recent').then((r) => setRecent(r.jobs)).catch(() => {});
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setJobs(null); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/api/jobs/search?q=${encodeURIComponent(term)}`)
        .then((r) => { setJobs(r.jobs); setError(null); })
        .catch((e) => e.message !== 'auth_required' && setError(e.status === 0 ? 'No signal – check your connection and try again.' : "Couldn't search just now. Please try again."))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // Only asks the phone for its location when the button is tapped. Nothing is kept.
  function findNear() {
    if (!navigator.geolocation) { setNear({ error: GEO_ERRORS[2] }); return; }
    setNear({ loading: true });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        api.post('/api/jobs/nearby', { lat: pos.coords.latitude, lng: pos.coords.longitude })
          .then(setNear)
          .catch((e) => e.message !== 'auth_required' && setNear({ error: e.status === 0 ? 'No signal – try again, or use the search box.' : "Couldn't look up nearby jobs just now. Use the search box." }));
      },
      (err) => setNear({ error: GEO_ERRORS[err.code] || GEO_ERRORS[2] }),
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 5 * 60 * 1000 }
    );
  }

  const showing = jobs !== null;
  const list = showing ? jobs : recent;

  return (
    <div className="space-y-4">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-6 w-6 -translate-y-1/2 text-steel" />
        <input
          className="input !pl-12 !text-lg"
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          placeholder="Job name or number"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search for a job by name or job number"
        />
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {canFindNearby && !showing && !near && (
        <button type="button" className="btn-ghost w-full" onClick={findNear}><PinIcon className="h-6 w-6" /> Jobs near me</button>
      )}
      {!showing && near?.loading && <div className="flex items-center justify-center gap-3 py-3 text-steel"><Spinner /> Finding jobs near you…</div>}
      {!showing && near?.error && <div className="space-y-3"><ErrorBox>{near.error}</ErrorBox><button type="button" className="btn-ghost w-full" onClick={findNear}>Try again</button></div>}
      {!showing && near?.groups && (
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="label !mb-0">Jobs near you</h2>
            <button type="button" className="text-sm text-steel underline" onClick={() => setNear(null)}>Hide</button>
          </div>
          {near.groups.length === 0 && (
            <p className="text-steel">No jobs found near you.{near.warming ? " We're still learning job locations – try again in a few minutes." : ''} Use the search box.</p>
          )}
          {near.groups.map((g) => (
            <div key={`${g.suburb}|${g.state}`} className="space-y-2">
              <h3 className="text-sm font-bold text-steel">{g.suburb}, {g.state}</h3>
              {g.jobs.map((j) => <JobCard key={j.id} job={j} to={`/job/${j.id}`} staff={showStage} />)}
            </div>
          ))}
          {near.groups.length > 0 && near.warming && <p className="text-xs text-steel">Some job locations are still being worked out, so this list may be incomplete.</p>}
          <p className="text-xs text-steel">Can't see it? Use the search box. Your location isn't stored.</p>
        </section>
      )}
      {loading && <div className="flex justify-center py-4"><Spinner /></div>}

      {!loading && (
        <>
          {!showing && list.length > 0 && <h2 className="label !mb-0">Your recent jobs</h2>}
          {!showing && list.length === 0 && !near?.groups && (
            <p className="py-8 text-center text-steel">Type a job name or number, e.g. <b>SQ10277</b>.</p>
          )}
          {showing && list.length === 0 && (
            <p className="py-8 text-center text-steel">
              No jobs found.{user.role === 'installer' && ' Only jobs that are in progress or complete can be found here.'}
            </p>
          )}
          <div className="space-y-3">
            {list.map((j) => <JobCard key={j.id} job={j} to={`/job/${j.id}`} staff={showStage} />)}
          </div>
        </>
      )}

      {canCreateJobs && (
        <Link to="/new" className={`${showing && list.length === 0 ? 'btn-primary' : 'btn-ghost'} w-full`}>
          + Can't find it? Create a new job
        </Link>
      )}
    </div>
  );
}
